/**
 * PrescriptionTemplateManager — Standalone card for managing saved Rx templates.
 *
 * Doctors can:
 *   - View all saved templates (NO cap — D1; search + category grouping)
 *   - Create / edit a template through the SHARED category-first save form
 *     (its medicines step is the typed-shorthand composer)
 *   - Duplicate an existing template (pre-fills create form for quick edits)
 *   - Delete existing templates
 *   - See medicine details for each template
 *
 * Supports item types: Medicine, Vaccine, Injection, and Lab Tests.
 * Lives as a top-level dashboard card (before Register Visit).
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import {
  listPrescriptionTemplates,
  deletePrescriptionTemplate,
} from '../../services/api/prescriptionTemplateApi';
import useLabEnabled from '../../hooks/useLabEnabled';
import useProcedureStore from '../../stores/procedureStore';
import TemplateSaveForm from './TemplateSaveForm';
import { useScope } from '../../context/PermissionContext';
import { STAFF_ROLES } from '../../constants/staff';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import {
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_DELETE_COPY,
  buildListGroups,
  listCountLine,
} from '../../utils/rxTemplates';
import { formatDoseSchedule as slotDisplay } from '../../utils/rxDosage';

// ── Item type helpers ───────────────────────────────────────────────

const ITEM_TYPE_META = {
  medicine:  { icon: '💊', label: 'Medicine',  color: '#0d9488' },
  vaccine:   { icon: '🛡️', label: 'Vaccine',   color: '#8b5cf6' },
  injection: { icon: '💉', label: 'Injection', color: '#3b82f6' },
};

/** The ONE way this card decides what a saved item is: the write-time
 *  `item_type` when a caller still holds it, else the stored `form`. */
function typeOfItem(m) {
  return m?.item_type || inferItemType(m?.form);
}

/** Infer item_type from the parser's `form` field */
function inferItemType(form) {
  if (!form) return 'medicine';
  const f = form.toLowerCase();
  if (f === 'vaccine') return 'vaccine';
  if (f === 'injection') return 'injection';
  return 'medicine';
}

function itemIcon(type) { return ITEM_TYPE_META[type]?.icon || '💊'; }
function itemLabel(type) { return ITEM_TYPE_META[type]?.label || 'Medicine'; }
function itemColor(type) { return ITEM_TYPE_META[type]?.color || '#0d9488'; }

export default function PrescriptionTemplateManager({ onBack, onSuccess }) {
  const { isDark } = useTheme();
  // D12/D15 — the same role rule the pad's template controls use. Editing a
  // template is a prescriber's action; it is hidden (never disabled) for
  // anyone else. `useScope` is the null-safe reader of PermissionContext.
  const { role } = useScope();
  const canEditTemplates = role === STAFF_ROLES.DOCTOR || role === STAFF_ROLES.ADMIN;
  const labEnabled = useLabEnabled();
  const { procedures, fetchProcedures } = useProcedureStore();
  const labTests = procedures.filter((p) => p.category === 'lab' && p.active !== false);
  useEffect(() => { if (labEnabled) fetchProcedures(); }, [labEnabled, fetchProcedures]);

  // ── State ─────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  // Browse (S9b): one search box over the same selector the pad reads.
  const [query, setQuery] = useState('');

  // The card no longer composes anything itself: "+ New template", Edit and
  // Duplicate all open the SHARED save form CATEGORY-FIRST (mockup 06), which
  // owns the category, the content (via TemplateMedicineComposer and its own
  // symptom/test inputs), the name and the same-name prompt.
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);   // edit mode
  const [seedPad, setSeedPad] = useState(null);                   // duplicate prefill
  const [seedName, setSeedName] = useState('');
  const [seedCategory, setSeedCategory] = useState('');

  // Delete confirmation — the shared in-app modal, never an inline red strip
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Expand template details
  const [expandedId, setExpandedId] = useState(null);


  // ── Styles ────────────────────────────────────────────────────────
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const inputBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)';
  const accentColor = '#0d9488';
  const textPrimary = isDark ? '#e8e9ed' : '#1f2937';
  const textSecondary = isDark ? '#9ca3af' : '#6b7280';
  const textMuted = isDark ? '#6b7280' : '#9ca3af';

  // ── Load templates ────────────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listPrescriptionTemplates();
      // `max_allowed` is always null now (D1 — there is no cap). It is
      // deliberately NOT read: `|| 5` here silently re-capped the card at five.
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── Open the shared save form ─────────────────────────────────────
  const openNewTemplate = useCallback(() => {
    setEditingTemplate(null); setSeedPad(null); setSeedName(''); setSeedCategory('');
    setShowSaveForm(true);
  }, []);

  const openEditTemplate = useCallback((template) => {
    setEditingTemplate(template); setSeedPad(null); setSeedName(''); setSeedCategory('');
    setShowSaveForm(true);
  }, []);

  const closeSaveForm = useCallback(() => {
    setShowSaveForm(false); setEditingTemplate(null); setSeedPad(null);
    setSeedName(''); setSeedCategory('');
  }, []);

  const handleSaved = useCallback((saved, mode) => {
    setTemplates((prev) => [saved, ...prev.filter((t) => t.template_id !== saved.template_id)]);
    closeSaveForm();
    setSuccessMsg(
      mode === 'new' ? `Saved as "${saved.name}".`
        : mode === 'edit' ? `Updated "${saved.name}".`
          : `Replaced "${saved.name}".`
    );
    setTimeout(() => setSuccessMsg(null), 3000);
  }, [closeSaveForm]);

  // ── Delete (D16) ──────────────────────────────────────────────────
  // Same confirmation component and the SAME strings the pad's list uses
  // (`TEMPLATE_DELETE_COPY`) — one set, not a second copy. On success the row
  // leaves list state locally; on failure it stays put with the failure line.
  const handleDelete = useCallback(async (template) => {
    try {
      await deletePrescriptionTemplate(template.template_id);
      setDeleteTarget(null);
      setTemplates((prev) => prev.filter((t) => t.template_id !== template.template_id));
      setSuccessMsg(TEMPLATE_DELETE_COPY.successToast(template.name));
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch {
      setDeleteTarget(null);
      setError(TEMPLATE_DELETE_COPY.failureToast);
    }
  }, []);

  // ── Duplicate → the same form, CREATE mode, prefilled from that template.
  // Its lab-catalogue tests ride along WITH THEIR NAMES (resolved here, the
  // only place that holds the catalogue) so the doctor can still untick one
  // off an old template — nothing can attach a NEW one (rx-r2 owner ruling).
  const handleDuplicate = useCallback((template) => {
    // No cap to check any more (D1) — a doctor may keep as many as they need.
    setEditingTemplate(null);
    setSeedPad({
      medicines: (template.medicines || []).map((m, i) => ({
        _id: `dup_${Date.now()}_${i}`,
        medicine: m.medicine || 'Unknown',
        medicine_input: m.medicine_input || m.medicine,
        strength: m.strength || null,
        form: m.form || null,
        item_type: m.item_type || inferItemType(m.form),
        dosage_schedule: m.dosage_schedule || null,
        frequency: m.frequency || null,
        timing: m.timing || null,
        duration: m.duration || null,
        notes: m.notes || null,
        is_resolved: m.is_resolved ?? true,
      })),
      symptoms: template.symptoms || [],
      investigations: template.investigations || [],
      labCatalogTests: (template.lab_catalog_ids || []).map((pid) => ({
        id: pid,
        name: labTests.find((x) => x.procedure_id === pid)?.name || pid,
      })),
      diagnosis: template.diagnosis || '',
      chiefComplaint: template.chief_complaint || '',
      doctorNotes: template.doctor_notes || '',
      followUpRequired: !!template.follow_up_required,
      followUpIn: template.follow_up_in || null,
    });
    setSeedName(`Copy of ${template.name}`.slice(0, 80));
    setSeedCategory(template.category || '');
    setShowSaveForm(true);
    setExpandedId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [labTests]);

  // Browse projection — the SAME shared function the pad's All-templates list
  // uses, so the card and the pad group, search and cap rows identically.
  const { selection, groups, shown, matched } = useMemo(
    () => buildListGroups(templates, { query }),
    [templates, query]
  );
  const countLine = listCountLine(shown, matched);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '1rem', maxWidth: '48rem', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1rem',
      }}>
        <div>
          <h2 style={{
            margin: 0, fontSize: '1.125rem', fontWeight: 700,
            color: textPrimary,
          }}>
            Prescription Templates
          </h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: textSecondary }}>
            Save what you write often and reuse it in one click.
          </p>
        </div>
        {canEditTemplates && (
          <button
            onClick={openNewTemplate}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: `linear-gradient(135deg, ${accentColor}, #115e59)`,
              color: '#fff',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + New template
          </button>
        )}
      </div>

      {/* Success / Error banners */}
      {successMsg && (
        <div style={{
          padding: '0.5rem 0.75rem', borderRadius: '0.5rem', marginBottom: '0.75rem',
          background: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.06)',
          border: '1px solid rgba(16,185,129,0.2)',
          fontSize: '0.75rem', color: '#10b981',
        }}>
          {successMsg}
        </div>
      )}
      {error && (
        <div style={{
          padding: '0.5rem 0.75rem', borderRadius: '0.5rem', marginBottom: '0.75rem',
          background: isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.2)',
          fontSize: '0.75rem', color: '#ef4444',
        }}>
          {error}
        </div>
      )}

      {showSaveForm && (
        <TemplateSaveForm
          template={editingTemplate}
          pad={seedPad}
          initialName={seedName}
          initialCategory={seedCategory}
          allowCompose
          existingTemplates={templates}
          onSaved={handleSaved}
          onClose={closeSaveForm}
          isDark={isDark}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title={TEMPLATE_DELETE_COPY.confirmTitle}
        message={deleteTarget ? TEMPLATE_DELETE_COPY.confirmBody(deleteTarget.name) : ''}
        ctaLabel={TEMPLATE_DELETE_COPY.confirmDangerButton}
        cancelLabel={TEMPLATE_DELETE_COPY.confirmKeepButton}
        variant="danger"
        icon="🗑"
        autoFocusCancel
        onConfirm={() => handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── Templates List ───────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: textMuted, fontSize: '0.8125rem' }}>
          Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '2.5rem 1rem',
          borderRadius: '0.75rem',
          border: `1px dashed ${borderColor}`,
          background: cardBg,
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💊</div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: textPrimary, marginBottom: '0.25rem' }}>
            No templates yet
          </div>
          <div style={{ fontSize: '0.75rem', color: textSecondary }}>
            Save your most-used prescriptions for quick one-tap access during visits
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <input
            type="text"
            value={query}
            aria-label="Search your templates"
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your templates…"
            style={{
              width: '100%', padding: '0.45rem 0.7rem', borderRadius: '0.5rem',
              border: `1px solid ${borderColor}`, background: inputBg,
              fontSize: '0.8125rem', color: textPrimary, outline: 'none',
              boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
          {countLine && (
            <div style={{ fontSize: '0.6875rem', color: textSecondary }}>{countLine}</div>
          )}
          {shown === 0 && (
            <div style={{ fontSize: '0.75rem', color: textSecondary, padding: '0.5rem 0' }}>
              {`Nothing matches "${query}" in your templates.`}
            </div>
          )}
          {groups.map((g) => (
          <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{
              fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', color: textSecondary, marginTop: '0.25rem',
            }}>
              {`${TEMPLATE_CATEGORY_LABELS[g.key]} · ${selection.counts[g.key]}`}
            </div>
            {g.rows.map((t) => {
            const isExpanded = expandedId === t.template_id;

            return (
              <div
                key={t.template_id}
                style={{
                  borderRadius: '0.75rem',
                  border: `1px solid ${isDark ? 'rgba(13,148,136,0.2)' : 'rgba(13,148,136,0.12)'}`,
                  background: isDark ? 'rgba(13,148,136,0.04)' : 'rgba(13,148,136,0.02)',
                  overflow: 'hidden',
                  transition: 'border-color 0.15s',
                }}
              >
                {/* Template header */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : t.template_id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.75rem 1rem', cursor: 'pointer', minHeight: '3.25rem',
                  }}
                >
                  <span style={{ fontSize: '1rem' }}>📋</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* D30 echo — the SAME name/summary scale the pad's list
                        uses, so the card and the list describe a template
                        identically. */}
                    <div style={{
                      fontWeight: 600, fontSize: '1rem', lineHeight: 1.35, color: textPrimary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {t.name}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: textSecondary, marginTop: '0.125rem', display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                      {(() => {
                        // The API never echoes `item_type` back (it is validated on
                        // write and dropped) — so the type is read the SAME way the
                        // expanded rows below read it: the stored `form`.
                        const meds = (t.medicines || []).filter(m => typeOfItem(m) === 'medicine').length;
                        const vaccs = (t.medicines || []).filter(m => typeOfItem(m) === 'vaccine').length;
                        const injs = (t.medicines || []).filter(m => typeOfItem(m) === 'injection').length;
                        const labs = t.investigations?.length || 0;
                        return (
                          <>
                            {meds > 0 && <span>💊 {meds}</span>}
                            {vaccs > 0 && <span>🛡️ {vaccs}</span>}
                            {injs > 0 && <span>💉 {injs}</span>}
                            {labs > 0 && <span>🧪 {labs}</span>}
                            {t.follow_up_required && t.follow_up_in && <span>📅 F/U {t.follow_up_in}</span>}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  {t.usage_count > 0 && (
                    <span style={{
                      padding: '0.15rem 0.5rem', borderRadius: '9999px',
                      fontSize: '0.5625rem', fontWeight: 600,
                      background: isDark ? 'rgba(13,148,136,0.15)' : 'rgba(13,148,136,0.08)',
                      color: accentColor,
                    }}>
                      Used {t.usage_count}x
                    </span>
                  )}
                  <span style={{ fontSize: '0.625rem', color: textMuted, transition: 'transform 0.15s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                    ▼
                  </span>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{
                    padding: '0 1rem 0.75rem',
                    borderTop: `1px solid ${isDark ? 'rgba(13,148,136,0.1)' : 'rgba(13,148,136,0.06)'}`,
                  }}>
                    {/* Items — medicines, vaccines, injections */}
                    {(t.medicines || []).map((med, i) => {
                      const type = typeOfItem(med);
                      const typeColor = itemColor(type);
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: '0.375rem',
                          padding: '0.375rem 0', flexWrap: 'wrap',
                          borderBottom: i < t.medicines.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` : 'none',
                        }}>
                          <span style={{ fontSize: '0.6875rem' }}>{itemIcon(type)}</span>
                          <span style={{ fontWeight: 600, fontSize: '0.75rem', color: textPrimary }}>{med.medicine}</span>
                          {med.strength && <span style={{ color: typeColor, fontSize: '0.6875rem' }}>{med.strength}</span>}
                          <span style={{
                            padding: '0.05rem 0.25rem', borderRadius: '0.2rem',
                            fontSize: '0.5rem', fontWeight: 600,
                            background: isDark ? `${typeColor}20` : `${typeColor}10`,
                            color: typeColor,
                          }}>
                            {itemLabel(type)}
                          </span>
                          {med.dosage_schedule && <span style={{ fontSize: '0.625rem', color: isDark ? '#60a5fa' : '#3b82f6' }}>{slotDisplay(med.dosage_schedule)}</span>}
                          {med.timing && <span style={{ fontSize: '0.625rem', color: textMuted }}>{med.timing}</span>}
                          {med.duration && <span style={{ fontSize: '0.625rem', color: textMuted }}>{med.duration}</span>}
                        </div>
                      );
                    })}

                    {/* Chief Complaint */}
                    {t.chief_complaint && (
                      <p style={{ margin: '0.375rem 0 0', fontSize: '0.6875rem', color: textSecondary }}>
                        🩺 <strong>C/O:</strong> {t.chief_complaint}
                      </p>
                    )}

                    {/* Diagnosis */}
                    {t.diagnosis && (
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.6875rem', color: textSecondary }}>
                        🔬 <strong>Dx:</strong> {t.diagnosis}
                      </p>
                    )}

                    {/* Investigations */}
                    {t.investigations?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.375rem' }}>
                        <span style={{ fontSize: '0.625rem', color: '#f59e0b', fontWeight: 600 }}>🧪</span>
                        {t.investigations.map((inv, i) => (
                          <span key={i} style={{
                            fontSize: '0.5625rem', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem',
                            background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.08)',
                            color: isDark ? '#fbbf24' : '#d97706', fontWeight: 500,
                          }}>{inv}</span>
                        ))}
                      </div>
                    )}

                    {/* Doctor notes */}
                    {t.doctor_notes && (
                      <p style={{ margin: '0.375rem 0 0', fontSize: '0.6875rem', fontStyle: 'italic', color: textSecondary }}>
                        📝 {t.doctor_notes}
                      </p>
                    )}

                    {/* Follow-up */}
                    {t.follow_up_required && (
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.6875rem', color: isDark ? '#34d399' : '#059669' }}>
                        📅 Follow-up{t.follow_up_in ? `: ${t.follow_up_in}` : ' advised'}
                      </p>
                    )}

                    {/* Action buttons — solid backgrounds in BOTH modes so
                        they are visible without hover (bk-947). */}
                    <div style={{ marginTop: '0.625rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Edit button (D15) — opens the SAME save form in edit
                          mode; saves with PUT to this id, keeping its usage
                          count. Hidden for non-prescribing roles. */}
                      {canEditTemplates && (
                        <button
                          onClick={() => openEditTemplate(t)}
                          aria-label={`Edit template "${t.name}"`}
                          style={{
                            padding: '0.3rem 0.75rem', borderRadius: '0.375rem',
                            border: `1px solid ${isDark ? 'rgba(148,163,184,0.5)' : 'rgba(71,85,105,0.45)'}`,
                            background: isDark ? '#1e293b' : '#f8fafc',
                            color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.6875rem', fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Edit
                        </button>
                      )}
                      {/* Duplicate — a CREATE affordance, so it follows the
                          same gate as Edit/Delete: hidden, never disabled, for
                          every non-prescribing role (D12; closes bk-964). */}
                      {canEditTemplates && (
                      <button
                        onClick={() => handleDuplicate(t)}
                        title="Copy this template as a new one"
                        style={{
                          padding: '0.3rem 0.75rem', borderRadius: '0.375rem',
                          border: `1px solid ${isDark ? 'rgba(45,212,191,0.5)' : 'rgba(13,148,136,0.45)'}`,
                          background: isDark ? '#1e293b' : '#f0fdfa',
                          color: isDark ? '#5eead4' : '#0f766e', fontSize: '0.6875rem', fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        📋 Duplicate
                      </button>
                      )}
                      {/* Delete (D16) — the shared in-app confirm with the
                          shared strings, never a native dialog. Hidden (never
                          disabled) for non-prescribing roles, like Edit. */}
                      {canEditTemplates && (
                      <button
                        onClick={() => setDeleteTarget(t)}
                        aria-label={TEMPLATE_DELETE_COPY.rowAriaLabel(t.name)}
                        style={{
                          padding: '0.3rem 0.75rem', borderRadius: '0.375rem',
                          border: `1px solid ${isDark ? 'rgba(248,113,113,0.5)' : 'rgba(220,38,38,0.45)'}`,
                          background: isDark ? '#1e293b' : '#fef2f2',
                          color: isDark ? '#fca5a5' : '#b91c1c', fontSize: '0.6875rem', fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        🗑 {TEMPLATE_DELETE_COPY.rowActionLabel}
                      </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
            })}
          </div>
          ))}
        </div>
      )}
    </div>
  );
}
